/**
 * Sélecteur d'utilisateurs MCP additionnels (hors responsables déjà couverts).
 *
 * Responsabilités :
 * - définir la règle métier d'exclusion (responsables, déjà sélectionnés)
 * - déléguer fetch, options et sélection à useUtilisateurMultiSelect
 */
import { UtilisateurMultiSelect } from 'lauria/components/common/UtilisateurMultiSelect'
import { useUtilisateurMultiSelect } from 'lauria/hooks/useUtilisateurMultiSelect'
import {
  SelectedTag,
  SelectedUtilisateur,
} from 'lauria/types/selectedUtilisateur'
import React, { useCallback, useMemo } from 'react'
import { FieldError } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

export type SelectedMcpUtilisateur = SelectedUtilisateur

type McpUtilisateurSelectProps = {
  selectedTags: SelectedTag[]
  selectedMcpUtilisateurs: SelectedMcpUtilisateur[]
  setSelectedMcpUtilisateurs: React.Dispatch<
    React.SetStateAction<SelectedMcpUtilisateur[]>
  >
  responsablesUtilisateurIds: number[] | null
  displayDefaultOption?: boolean
  width?: string
  error?: FieldError
}

export const McpUtilisateurSelect: React.FC<McpUtilisateurSelectProps> = ({
  selectedTags,
  selectedMcpUtilisateurs,
  setSelectedMcpUtilisateurs,
  responsablesUtilisateurIds,
  displayDefaultOption = true,
  width = '[300px]',
  error,
}) => {
  const { t } = useTranslation()
  const excludedResponsableIds = useMemo(
    () => new Set(responsablesUtilisateurIds ?? []),
    [responsablesUtilisateurIds]
  )

  const isUtilisateurExcluded = useCallback(
    (utilisateur: { idUtilisateur: number }) =>
      excludedResponsableIds.has(utilisateur.idUtilisateur) ||
      selectedMcpUtilisateurs.some(
        (mcpUtilisateur) => mcpUtilisateur.id === utilisateur.idUtilisateur
      ),
    [excludedResponsableIds, selectedMcpUtilisateurs]
  )

  const { options, isLoading, selectValue, handleSelect } =
    useUtilisateurMultiSelect({
      selectedTags,
      onSelect: setSelectedMcpUtilisateurs,
      isUtilisateurExcluded,
    })

  return (
    <UtilisateurMultiSelect
      placeholder={t('applications.form.access.mcpUtilisateursPlaceholder')}
      fieldName="mcpUtilisateurs"
      options={options}
      isLoading={isLoading}
      value={selectValue}
      onChange={handleSelect}
      displayDefaultOption={displayDefaultOption}
      width={width}
      error={error}
    />
  )
}
